import { hexToPptxColor } from "../export/pptxUnits.js";
export function emptyChartData() {
    return {
        type: "bar",
        series: [
            {
                label: "Serie 1",
                points: [
                    { x: "A", y: 10 },
                    { x: "B", y: 20 },
                    { x: "C", y: 15 },
                ],
            },
        ],
    };
}
export function normalizeChartData(data) {
    const type = data.type === "line" || data.type === "pie" ? data.type : "bar";
    const series = (data.series ?? [])
        .map((s) => ({
        label: String(s.label ?? "Serie").trim() || "Serie",
        points: (s.points ?? [])
            .map((p) => ({
            x: String(p.x ?? "").trim(),
            y: Number.isFinite(Number(p.y)) ? Number(p.y) : 0,
        }))
            .filter((p) => p.x.length > 0),
    }))
        .filter((s) => s.points.length > 0);
    return {
        type,
        series: series.length ? series : emptyChartData().series,
    };
}
export function getLayerChart(layer) {
    const raw = layer.style?.chart;
    if (!raw || typeof raw !== "object")
        return emptyChartData();
    return normalizeChartData(raw);
}
export function setLayerChart(layer, chart) {
    return {
        ...layer,
        style: { ...layer.style, chart: normalizeChartData(chart) },
    };
}
export function collectChartCategories(data) {
    const normalized = normalizeChartData(data);
    const seen = new Set();
    const categories = [];
    for (const series of normalized.series) {
        for (const point of series.points) {
            if (!seen.has(point.x)) {
                seen.add(point.x);
                categories.push(point.x);
            }
        }
    }
    return categories;
}
export function chartValueAt(series, category) {
    return series.points.find((p) => p.x === category)?.y ?? 0;
}
export function chartValueExtents(data) {
    const normalized = normalizeChartData(data);
    let min = 0;
    let max = 0;
    for (const series of normalized.series) {
        for (const point of series.points) {
            min = Math.min(min, point.y);
            max = Math.max(max, point.y);
        }
    }
    if (max === min)
        max = min + 1;
    return { min, max };
}
export function setChartType(data, type) {
    return normalizeChartData({ ...data, type });
}
export function setSeriesLabel(data, seriesIndex, label) {
    const next = normalizeChartData(data);
    if (seriesIndex < 0 || seriesIndex >= next.series.length)
        return next;
    next.series[seriesIndex] = { ...next.series[seriesIndex], label };
    return next;
}
export function setChartPoint(data, seriesIndex, pointIndex, patch) {
    const next = normalizeChartData(data);
    const series = next.series[seriesIndex];
    if (!series)
        return next;
    const points = [...series.points];
    const current = points[pointIndex] ?? { x: `Cat ${pointIndex + 1}`, y: 0 };
    points[pointIndex] = {
        x: patch.x !== undefined ? patch.x : current.x,
        y: patch.y !== undefined ? patch.y : current.y,
    };
    next.series[seriesIndex] = { ...series, points };
    return next;
}
export function addChartSeries(data) {
    const next = normalizeChartData(data);
    const categories = collectChartCategories(next);
    const points = categories.map((x) => ({ x, y: 0 }));
    next.series.push({
        label: `Serie ${next.series.length + 1}`,
        points: points.length ? points : [{ x: "A", y: 0 }],
    });
    return normalizeChartData(next);
}
export function removeChartSeries(data, seriesIndex) {
    const next = normalizeChartData(data);
    if (next.series.length <= 1)
        return next;
    next.series.splice(seriesIndex, 1);
    return next;
}
export function addChartPoint(data, seriesIndex) {
    const next = normalizeChartData(data);
    const series = next.series[seriesIndex];
    if (!series)
        return next;
    const label = `Cat ${series.points.length + 1}`;
    series.points.push({ x: label, y: 0 });
    return normalizeChartData(next);
}
export function removeChartPoint(data, seriesIndex, pointIndex) {
    const next = normalizeChartData(data);
    const series = next.series[seriesIndex];
    if (!series || series.points.length <= 1)
        return next;
    series.points.splice(pointIndex, 1);
    return normalizeChartData(next);
}
export function chartSeriesColors(theme, count) {
    const base = [
        theme.palette.accent,
        theme.palette.text,
        theme.palette.muted,
        theme.palette.surface,
    ];
    return Array.from({ length: count }, (_, i) => base[i % base.length]);
}
export function buildPptxChartPayload(data) {
    const normalized = normalizeChartData(data);
    const categories = collectChartCategories(normalized);
    return {
        type: normalized.type,
        series: normalized.series.map((s) => ({
            name: s.label,
            labels: normalized.type === "pie" ? s.points.map((p) => p.x) : categories,
            values: normalized.type === "pie"
                ? s.points.map((p) => p.y)
                : categories.map((cat) => chartValueAt(s, cat)),
        })),
    };
}
export function chartLayerFromData(chart, partial) {
    return {
        type: "chart",
        x: 0.52,
        y: 0.18,
        w: 0.42,
        h: 0.62,
        z: partial?.z ?? 30,
        rotation: 0,
        content: "",
        style: { chart: normalizeChartData(chart) },
        ...partial,
    };
}
export function pptxChartColors(theme, count) {
    return chartSeriesColors(theme, count).map((c) => hexToPptxColor(c));
}
